import { useEffect } from 'react'

interface UseAnnotationShortcutsParams {
  isActive: boolean
  onSubmit: () => void
  onNextImage?: () => void
  onPrevImage?: () => void
  undo: () => void
  redo: () => void
}

export function useAnnotationShortcuts({
  isActive,
  onSubmit,
  onNextImage,
  onPrevImage,
  undo,
  redo,
}: UseAnnotationShortcutsParams): void {
  useEffect(() => {
    if (!isActive) return
    const handler = (e: KeyboardEvent) => {
      // Ignore when typing in editable fields
      const target = e.target as HTMLElement | null
      if (target) {
        const tag = target.tagName
        const isEditableEl =
          target.isContentEditable || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'
        if (isEditableEl) return
      }
      // Enter submit
      if (e.key === 'Enter') {
        e.preventDefault()
        onSubmit()
        return
      }
      // Shift+N next, Shift+P previous
      if (e.shiftKey && (e.key === 'N' || e.key === 'n')) {
        e.preventDefault()
        onNextImage?.()
        return
      }
      if (e.shiftKey && (e.key === 'P' || e.key === 'p')) {
        e.preventDefault()
        onPrevImage?.()
        return
      }
      // Ctrl/Cmd+Z undo, Ctrl/Cmd+Shift+Z redo
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key === 'z') {
        e.preventDefault()
        undo()
        return
      }
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'z') {
        e.preventDefault()
        redo()
        return
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [isActive, onSubmit, onNextImage, onPrevImage, undo, redo])
}
